<template>
	<div
		v-if="ready"
		class="container-fluid"
	>

		<div class="row center">
			<div class="col-lg-12">
				<h1 class="main-content-title">
					{{ activeTab }} ({{ pagination.total }})
					<el-button
						v-if="!isTeamViewer"
						@click="userTeam.plan.expired
							? openTrialExpiredDialog()
							: isPlanExceed(costs.storageUsedBytes, 'maxStorage')
								? openPlanLimitDialog()
								: openUploadDialog()
						"
						type="primary"
						plain
					>
						{{ translations.labels.addNew }}
					</el-button>
				</h1>
			</div>
		</div>

		<div class="row center">
			<div class="col-lg-12">
				<div class="box">

					<Info
						type="media"
						:text="translations.labels.mediaText"
					/>

					<div class="box-body">

						<div class="table-header no-border">
							<div class="row">
								<div class="col-lg-12">
									<div class="flex-between">
										<SearchElement
											:disabled="images.length === 0 || videos.length === 0"
											:processing="processing.search"
										/>
									</div>
								</div>
							</div>
							<SearchFilter
								v-if="images.length || videos.length"
								:search="search"
							/>
						</div>

						<div style="position: relative">

							<div
								v-if="images.length || videos.length"
								class="media-view"
							>
								<el-tooltip
									:content="translations.labels.tableView"
									placement="top"
								>
									<el-button
										@click="view = 'table'"
										:disabled="view === 'table'"
										icon="el-icon-s-unfold"
										size="mini"
										plain
									/>
								</el-tooltip>
								<el-tooltip
									:content="translations.labels.gridView"
									placement="top"
								>
									<el-button
										@click="view = 'grid'"
										:disabled="view === 'grid'"
										icon="el-icon-s-grid"
										size="mini"
										plain
									/>
								</el-tooltip>
							</div>

							<el-tabs
								v-model="activeTab"
								@tab-click="changeTab"
								type="card"
							>

								<el-tab-pane name="images">
									<span slot="label">
										<i class="el-icon-camera"></i> {{ translations.labels.images }}
									</span>
									<MediaTab
										:collection="images"
										:loading="loading"
										:buttonLoading="buttonLoading"
										:pagination="pagination"
										:view="view"
										:inDialog="false"
									/>
								</el-tab-pane>

								<el-tab-pane name="videos">
									<span slot="label">
										<i class="el-icon-video-camera"></i> {{ translations.labels.videos }}
									</span>
									<MediaTab
										:collection="videos"
										:loading="loading"
										:buttonLoading="buttonLoading"
										:pagination="pagination"
										:view="view"
										:inDialog="false"
									/>
								</el-tab-pane>

							</el-tabs>

						</div>

					</div>

				</div>
			</div>
		</div>

		<!-- upload dialog -->
		<MediaUpload :show="dialog.upload" />
		<!-- /upload dialog -->

		<!-- delete dialog -->
		<DeleteConfirmation
			:show="dialog.delete"
			:processing="processing.delete"
			:message="translations.messages.warningRemoveFile"
			:label="translations.labels.deleteFile"
			confirmAction="proceed-delete"
			cancelAction="cancel-delete"
		/>
		<!-- /delete dialog -->

		<!-- edit dialog -->
		<el-dialog
			:title="translations.labels.editFile"
			@closed="closedUpdateDialog"
			:visible.sync="dialog.update"
			:show-close="false"
			:close-on-click-modal="false"
			width="500px"
		>
			<div v-if="tempData" class="row preview">
				<div class="col-lg-7">
					<div v-if="tempData.mediaType === 'IMAGE'" class="preview-image">
						<img :src="tempData.path" class="preview-image" />
					</div>
					<div v-else class="preview-video">
						<video width="320" height="240" controls>
							<source :src="tempData.path" type="video/mp4" />
						</video>
					</div>
				</div>
				<div class="col-lg-5">
					<div class="preview-data">
						<div v-if="tempData.resolution" class="row">
							<div class="col-lg-12 preview-data-label">
								{{ translations.labels.resolution }}:
							</div>
							<div class="col-lg-12 preview-data-value">
								{{ tempData.resolution }}
							</div>
						</div>
						<div v-if="tempData.size" class="row">
							<div class="col-lg-12 preview-data-label">
								{{ translations.labels.size }}:
							</div>
							<div class="col-lg-12 preview-data-value">
								{{ convertBytes(tempData.size) }}
							</div>
						</div>
						<div v-if="tempData.videoLength" class="row">
							<div class="col-lg-12 preview-data-label">
								{{ translations.labels.duration }}:
							</div>
							<div class="col-lg-12 preview-data-value">
								{{ tempData.videoLength }}s
							</div>
						</div>
						<div v-if="tempData.mimeType" class="row">
							<div class="col-lg-12 preview-data-label">
								{{ translations.labels.type }}:
							</div>
							<div class="col-lg-12 preview-data-value text-uppercase">
								{{ tempData.mimeType }}
							</div>
						</div>
						<div v-if="tempData.createdAt" class="row">
							<div class="col-lg-12 preview-data-label">
								{{ translations.labels.uploadedAt }}:
							</div>
							<div class="col-lg-12 preview-data-value">
								{{ convertDate(tempData.createdAt, 'date') }}
							</div>
						</div>
					</div>
				</div>
			</div>
			<el-form
				@submit.prevent.native="submitForm('update')"
				:model="form"
				label-position="top"
				label-width="100%"
				ref="update"
			>
				<div class="row margin-top-10">
					<div class="col-lg-12">
						<el-form-item :label="translations.labels.fileName" class="alt-label">
							<el-input
								v-model="form.fileName"
								:placeholder="translations.labels.enterTitle"
								:disabled="isTeamViewer"
								:suffix-icon="isTeamViewer ? '' : 'el-icon-edit'"
								type="text"
							/>
						</el-form-item>
					</div>
				</div>
				<div class="row">
					<div class="col-lg-12 align-right">
						<el-form-item class="no-margin-bottom">
							<el-button
								:loading="processing.update"
								:disabled="isDisabledEdit || isTeamViewer"
								:type="isDisabledEdit || isTeamViewer ? 'info' : 'primary'"
								native-type="submit"
								plain
							>
								{{ translations.labels.update }}
							</el-button>
							<el-button
								@click="closeUpdateDialog()"
								plain
							>
								{{ translations.labels.cancel }}
							</el-button>
						</el-form-item>
					</div>
				</div>
			</el-form>
		</el-dialog>
		<!-- /edit dialog -->

	</div>
</template>

<script>
import axios from 'axios'
import { EventBus } from '@/services/event-bus'

import { _getMedia, _updateMedia, _deleteMedia } from '@/services/api/media'
import { _getDevicesCosts } from '@/services/api/devices'

import convertBytes from '@/services/mixins/convertBytes'
import convertDate from '@/services/mixins/convertDate'

export default {
	name: 'MediaView',
	metaInfo () {
		return {
			title: this.translations.navigation.media
		}
	},
	mixins: [
		convertBytes,
		convertDate
	],
	components: {
		DeleteConfirmation: () => import( /* webpackPrefetch: true */ '@/views/Private/Components/Dialog/Delete'),
		Info: () => import( /* webpackPrefetch: true */ '@/views/Private/Components/Misc/Info'),
		MediaTab: () => import( /* webpackPrefetch: true */ '@/views/Private/Components/Media/Tab'),
		MediaUpload: () => import( /* webpackPrefetch: true */ '@/views/Private/Components/Media/Upload'),
		SearchElement: () => import( /* webpackPrefetch: true */ '@/views/Private/Components/Search/Element'),
		SearchFilter: () => import( /* webpackPrefetch: true */ '@/views/Private/Components/Search/Filter')
	},
	data () {
		return {
			activeTab: 'images',
			buttonLoading: false,
			deleteId: null,
			dialog: {
				delete: false,
				upload: false,
				update: false
			},
			form: {
				id: null,
				fileName: null
			},
			images: [],
			loading: false,
			pagination: {
				total: null,
				pageSize: this.$config.paginationSize.grid,
				currentPage: 1,
				sortCriteria: 'asc'
			},
			processing: {
				delete: false,
				upload: false,
				update: false,
				search: false
			},
			search: {
				field: 'fileName',
				value: '',
				filter: false,
				sortBy: 'fileName'
			},
			tempData: null,
			tempTab: null,
			upload: {
				path: '',
				headers: {
					Application: this.$store.state.axiosToken,
					Authorization: `Bearer ${this.$store.state.auth.access_token}`
				}
			},
			view: 'table',
			videos: [],
			costs: {}
		}
	},
	computed: {
		isDisabledEdit () {
			return !this.form.fileName
		},
		isDisabledSearch () {
			return !this.search.value
		}
	},
	watch: {
		'view' () {
			this.pagination.pageSize = this.view === 'grid' ? this.$config.paginationSize.grid : this.$config.paginationSize.table
			this.pagination.currentPage = 1
			this.ajaxLoadMedia()
		}
	},
	created () {
		this.objectLength(this.userTeam) ? this.ajaxLoadData() : this.redirectTo('/team/fallback')
	},
	mounted () {
		EventBus.$on('open-edit', (data) => this.openUpdateDialog(data))
		EventBus.$on('open-delete', (data) => this.openDeleteDialog(data))
		EventBus.$on('change-pagination', (data) => this.changePage(data))
		EventBus.$on('reset-search', () => this.resetSearch())
		EventBus.$on('submit-search', (data) => this.submitSearch(data))
		EventBus.$on('cancel-delete', () => this.closeDeleteDialog())
		EventBus.$on('proceed-delete', () => this.ajaxDeleteFile())
		EventBus.$on('close-upload', () => this.closeUploadDialog())
		EventBus.$on('upload-success', (data) => this.uploadSuccess(data))
	},
	destroyed () {
		EventBus.$off('open-edit')
		EventBus.$off('open-delete')
		EventBus.$off('change-pagination')
		EventBus.$off('reset-search')
		EventBus.$off('submit-search')
		EventBus.$off('cancel-delete')
		EventBus.$off('proceed-delete')
		EventBus.$off('close-upload')
		EventBus.$off('upload-success')
    },
	methods: {

		ajaxLoadData () {
			const data = {
				searchField: this.search.field,
				searchValue: this.search.value,
				currentPage: this.pagination.currentPage,
				pageSize: this.pagination.pageSize,
				sortBy: 'createdAt',
				sortCriteria: 'desc'
			}
			axios.all([
				_getMedia (data, this.activeTab),
				_getDevicesCosts()
			])
			.then(axios.spread((res1, res2) => {
				this.costs = res2
				this.setupMedia(res1)
			}))
			.catch(error => {
				const errorCode = error.response.data.status
				setTimeout (() => {
					this.displayMessage(this.translations.statusCodes[errorCode] ? this.translations.statusCodes[errorCode] : this.translations.statusCodes['default'], 'error')
				}, 200)
			})
		},
		ajaxLoadMedia () {
			const data = {
				searchField: this.search.field,
				searchValue: this.search.value,
				currentPage: this.pagination.currentPage,
				pageSize: this.pagination.pageSize,
				sortBy: 'createdAt',
				sortCriteria: 'desc'
			}
			_getMedia (data, this.activeTab)
				.then(response => {
					this.setupMedia(response)
				})
				.catch(error => {
					const errorCode = error.response.data.status
					setTimeout (() => {
						this.loading = false
						this.buttonLoading = false
						this.displayMessage(this.translations.statusCodes[errorCode] ? this.translations.statusCodes[errorCode] : this.translations.statusCodes['default'], 'error')
					}, 200)
				})
		},
		ajaxDeleteFile () {
			this.loading = true
			this.processing.delete = true
			_deleteMedia (this.deleteId)
				.then(() => {
					this.ajaxLoadMedia()
					setTimeout (() => {
						this.closeDeleteDialog()
						this.displayMessage(this.translations.messages.successDeleteFile, 'success')
						this.processing.delete = false
					}, 200)
				})
				.catch(error => {
					const errorCode = error.response.data.status
					this.deleteId = null
					setTimeout (() => {
						this.displayMessage(this.translations.statusCodes[errorCode] ? this.translations.statusCodes[errorCode] : this.translations.statusCodes['default'], 'error')
						this.processing.delete = false
					}, 200)
				})
		},
		ajaxUpdateFile () {
			this.processing.update = true
			_updateMedia(this.form)
				.then(() => {
					this.ajaxLoadMedia()
					setTimeout (() => {
						this.closeUpdateDialog()
						this.displayMessage(this.translations.messages.successUpdateFile, 'success')
					}, 200)
				})
				.catch(error => {
					const errorCode = error.response.data.status
					setTimeout (() => {
						this.displayMessage(this.translations.statusCodes[errorCode] ? this.translations.statusCodes[errorCode] : this.translations.statusCodes['default'], 'error')
						this.processing.update = false
					}, 200)
				})
		},

		submitForm (form) {
			this.$refs[form].validate((valid) => {
				if (valid) {
					if (form === 'update') {
						this.ajaxUpdateFile()
					}
				}
			})
		},
		changeTab () {
			this.loading = true
			this.buttonLoading = false
			this.pagination.currentPage = 1
			this.ajaxLoadMedia()
		},
		openUploadDialog () {
			this.dialog.upload = true
		},
		closeUploadDialog () {
			this.dialog.upload = false
		},
		uploadSuccess (data) {
			this.activeTab = data.type
			setTimeout (() => {
				this.displayMessage(this.translations.messages.successUploadFile, 'success')
				this.ajaxLoadMedia()
			}, 200)
		},
		openDeleteDialog (id) {
			this.dialog.delete = true
			this.deleteId = id
		},
		closeDeleteDialog () {
			this.dialog.delete = false
			this.processing.delete = false
			this.deleteId = null
		},
		submitSearch (data) {
			this.search.value = data
			this.search.filter = true
			this.processing.search = true
			this.loading = true
			this.ajaxLoadMedia()
		},
		resetSearch () {
			this.pagination.currentPage = 1
			this.processing.search = false
			this.search = {
				filter: false,
				searchField: 'fileName',
				value: '',
				sortBy: 'fileName',
			}
			this.loading = true
			this.ajaxLoadMedia()
		},
		openUpdateDialog (value) {
			this.tempData = value
			this.form = {
				id: value.id,
				fileName: value.fileName
			}
			this.dialog.update = true
		},
		closeUpdateDialog () {
			this.dialog.update = false
			this.processing.update = false
		},
		closedUpdateDialog () {
			this.tempData = null
			this.form = {
				id: null,
				fileName: null
			}
		},
		setupMedia (response) {
			if (this.pagination.currentPage === 1) {
				this[this.activeTab] = []
			}
			if (this.view === 'grid') {
				this[this.activeTab] = [...this[this.activeTab], ...response.responseList]
			} else {
				this[this.activeTab] = response.responseList
			}
			this.pagination = {
				total: response.total,
				pageSize: response.pageSize,
				currentPage: response.currentPage
			}
			setTimeout (() => {
				this.removeLoader()
				this.loading = false
				this.buttonLoading = false
				this.processing.search = false
			}, 200)
		},
		changePage (page) {
			if (this.view === 'table') {
				this.loading = true
			} else {
				this.buttonLoading = true
			}
			this.pagination.currentPage = page
			this.ajaxLoadMedia()
		}

	}
}
</script>
